import {
  HTTP_ERROR_NOT_FOUND,
  HTTP_ERROR_BAD_REQUEST,
  HTTP_ERROR_INVALID_JSON,
  HTTP_ERROR_INTERNAL_SERVER,
  HTTP_ERROR_UNAUTHORIZED,
} from './constants.js';

import * as moment from 'moment';

const SERVER_TIME_HEADER = 'X-Server-Time';
const CSRF_HEADER = 'X-CSRF-Token';
let CSRF_TOKEN = '';

let timeOffsets = [];

// setOffset sets the time offset of the client based on server time.
function setOffset(currentOffset) {
  if (timeOffsets.length >= 10 || isNaN(currentOffset)) return; // Assuming 10 samples is representative
  timeOffsets.push(currentOffset);

  if (timeOffsets.length < 3) return; // Wait until we have some data to adjust

  let relevant = timeOffsets
    .sort()
    .reverse()
    .slice(0, parseInt(timeOffsets.length * 0.7));
  let offset = relevant.reduce((a, b) => a + b) / relevant.length;

  if (Math.abs(offset) > 1000) {
    moment.now = function() {
      return offset + Date.now();
    };
  } else {
    moment.now = function() {
      return +new Date();
    };
  }

  return offset;
}

export async function postAccessCode(source, id, code, token, checkin) {
  const url = '/access/code/' + source;
  return _netPost(url, {
    code,
    token,
    event_id: id,
    checkin,
  });
}

export async function postStreamLicense(token, challenge, position, playerStatus) {
  const url = '/drm/license';
  return _netPost(url, {
    jwt: token,
    challenge,
    position: parseInt(position, 10),
    player_state: playerStatus,
  });
}

export async function getSourceRegistry(source, id) {
  const url = '/stream/summary/' + source + '/' + id;
  return _netGet(url);
}

export async function getSourceRegistryPrivate(source, id) {
  // requires authentication (cookie or Authorization header)
  const url = '/stream/registration/' + source + '/' + id;
  return _netGet(url);
}

export async function getTheme(themeSlug) {
  const url = '/theme/settings/' + themeSlug;
  return _netGet(url);
}

export async function postChatMessage(viewer_token, source, id, name, message, room) {
  const body = {
    name,
    message,
    viewer_token,
    room,
  };
  return _netPost('/chat/message/' + source + '/' + id, body, viewer_token);
}

export async function getChatHistory(viewer_token, source, id, room) {
  let query = '';
  if (room) {
    query += 'room=' + encodeURIComponent(room);
  }
  // not yet implemented server side
  // if (before) {
  //   query += 'before=' + encodeURIComponent(before);
  // }
  if (query) {
    query = '?' + query;
  }
  return _netGet('/chat/message/' + source + '/' + id + query, viewer_token);
}

export async function getPinnedMessage(viewer_token, source, id) {
  return _netGet('/chat/message/pinned/' + source + '/' + id, viewer_token);
}

export async function postOrganizerViewerToken(source, id) {
  return _netPost('/auth/viewer/' + source + '/' + id, {});
}

export async function postAdminMessage(source, id, moderator_name, message, pin, time, hide_chat) {
  // auth handled by organizer cookie - not Authorization header
  const body = {
    moderator_name,
    message,
    pin,
    time: time || null,
    hide_chat: !!hide_chat,
  };
  return _netPost('/chat/moderator/message/' + source + '/' + id, body);
}

export async function postBanUser(source, id, subject, name) {
  // auth handled by organizer cookie - not Authorization header
  return _netPost('/chat/moderator/ban/' + source + '/' + id, {
    subject,
    name,
  });
}

export async function postUnbanUser(source, id, subject) {
  // auth handled by organizer cookie - not Authorization header
  return _netPost('/chat/moderator/unban/' + source + '/' + id, {
    subject,
  });
}

export async function postBanAccess(source, id, subject, name) {
  // auth handled by organizer cookie - not Authorization header
  return _netPost('/access/moderator/ban/' + source + '/' + id, {
    subject,
    name,
  });
}

export async function getBannedChatUsers(source, id) {
  const url = '/chat/moderator/banned/' + source + '/' + id;
  return _netGet(url);
}

export async function deleteChatMessage(source, id, messageId) {
  return _netDelete('/chat/moderator/message/' + source + '/' + id + '/' + messageId);
}

export async function postReaction(viewer_token, source, event_id, reaction, position) {
  const body = {
    source,
    event_id,
    reaction,
    position,
  };
  return _netPost('/chat/reaction/' + source + '/' + event_id, body, viewer_token);
}

export async function putUpdateStreamSettings(source, id, provider, updates) {
  // auth handled by organizer cookie - not Authorization header
  updates.provider = provider;
  return _netPut('/stream/registration/' + source + '/' + id, updates);
}

export async function getAnalytics(source, id, metric) {
  // auth handled by organizer cookie - not Authorization header
  return _netGet('/analytics/metric/' + metric + '/' + source + '/' + id);
}

export async function getBroadcasterSettings(source, id) {
  return _netGet('/chat/settings/' + source + '/' + id);
}

export async function getRoom(source, eventId, roomId, token) {
  return _netGet('/rooms/room/' + roomId, token);
}

export async function postRoom(source, eventId, roomId, name, token) {
  let body = {
    source: source,
    event_id: eventId,
    room_id: roomId,
    room_name: name,
  };
  return _netPost('/rooms/room', body, token);
}

export async function getAllRules(source, eventId) {
  return _netGet('/rules/stream/' + source + '/' + eventId);
}

export async function postAddRule(body, token) {
  return _netPost('/rules/rule', body, token);
}

export async function deleteRule(rule) {
  return _netDelete('/rules/rule/' + rule);
}

export async function getHLS(url) {
  // Retrieve the HLS feed directly
  return fetch(url, {
    method: 'GET',
    mode: 'cors',
  });
}

export async function postRevokeMasterAccess(source, id, assetID) {
  return _netPost(`/stream/registration/${source}/${id}/downloads/${assetID}/revoke`, {});
}

export async function postEnableMasterAccess(source, id, assetID) {
  return _netPost(`/stream/registration/${source}/${id}/downloads/${assetID}/enable`, {});
}

export async function getStreamAsset(source, id, assetID) {
  return _netGet(`/stream/registration/${source}/${id}/downloads/${assetID}`);
}

export async function getStreamAssets(source, id) {
  return _netGet(`/stream/registration/${source}/${id}/assets`);
}

export async function patchConvertToVOD(source, id, assetIndex, body) {
  return _netPatch('/stream/registration/' + source + '/' + id + '/convert/' + assetIndex, body);
}

export async function postSubtitle(source, eventId, formData) {
  return _netPostFormData(
    '/stream/registration/' + source + '/' + eventId + '/subtitles',
    formData
  );
}

export async function getSubtitles(source, eventId) {
  return _netGet('/stream/registration/' + source + '/' + eventId + '/subtitles');
}

export function deleteSubtitle(source, eventId, trackId) {
  return _netDelete('/stream/registration/' + source + '/' + eventId + '/subtitles/' + trackId);
}

export async function getMuxEvents(source, eventId) {
  return _netGet('/stream/registration/' + source + '/' + eventId + '/mux/events');
}

export async function getOrgEvents(source, orgId, start, end) {
  return _netGet('/stream/org/events/' + source + '/' + orgId, null, { start, end });
}

export async function putCompleteStream(source, eventId) {
  return _netPut('/stream/registration/' + source + '/' + eventId + '/complete', null);
}

// Network helpers
// url string URL forfor the network endpoint
// jwt string [optional] JWT of the viewer or organizer
async function _netGet(url, jwt, queryParams) {
  let headers = {
    'Content-Type': 'application/json',
  };
  if (jwt) {
    headers['Authorization'] = 'Bearer ' + jwt;
  }
  let base = window.location.protocol + '//' + window.location.host;
  let u = new URL(url, base);
  if (!!queryParams) {
    for (const key in queryParams) {
      u.searchParams.set(key, queryParams[key]);
    }
  }
  const response = await fetch(u, {
    method: 'GET',
    mode: 'cors',
    headers,
  });
  const payload = await _parse(response);
  return Promise.resolve(payload);
}

async function _netPost(url, body, jwt) {
  let headers = {
    'Content-Type': 'application/json',
  };
  if (jwt) {
    headers['Authorization'] = 'Bearer ' + jwt;
  }
  if (CSRF_TOKEN) {
    headers[CSRF_HEADER] = CSRF_TOKEN;
  }
  const response = await fetch(url, {
    method: 'POST',
    mode: 'cors',
    headers,
    body: JSON.stringify(body),
  });
  const payload = await _parse(response);
  return Promise.resolve(payload);
}

async function _netPostFormData(url, formData, jwt) {
  let headers = {};
  if (jwt) {
    headers['Authorization'] = 'Bearer ' + jwt;
  }
  if (CSRF_TOKEN) {
    headers[CSRF_HEADER] = CSRF_TOKEN;
  }
  const response = await fetch(url, {
    method: 'POST',
    mode: 'cors',
    headers,
    body: formData,
  });
  const payload = await _parse(response);
  return Promise.resolve(payload);
}

async function _netDelete(url, jwt) {
  let headers = {
    'Content-Type': 'application/json',
  };
  if (jwt) {
    headers['Authorization'] = 'Bearer ' + jwt;
  }
  if (CSRF_TOKEN) {
    headers[CSRF_HEADER] = CSRF_TOKEN;
  }
  const response = await fetch(url, {
    method: 'DELETE',
    mode: 'cors',
    headers,
  });
  const payload = await _parse(response);
  return Promise.resolve(payload);
}

async function _parse(response) {
  // Adjust client time if out of sync with server
  const time_header = response.headers.get(SERVER_TIME_HEADER);
  if (!!time_header) {
    var offset = parseInt(time_header) - Date.now();
    setOffset(offset);
  }
  // Set CSRF header to use for next network call
  const csrf_header = response.headers.get(CSRF_HEADER);
  if (!!csrf_header) {
    CSRF_TOKEN = csrf_header;
  }
  let payload = {};
  try {
    payload = await response.json();
  } catch (e) {
    throw new Error(HTTP_ERROR_BAD_REQUEST);
  }
  if (!payload) {
    throw new Error(HTTP_ERROR_INVALID_JSON);
  }
  if (!response.ok) {
    throw payload;
  }
  return payload;
}

async function _netPut(url, body) {
  let headers = {
    'Content-Type': 'application/json',
  };
  if (CSRF_TOKEN) {
    headers[CSRF_HEADER] = CSRF_TOKEN;
  }
  const response = await fetch(url, {
    method: 'PUT',
    mode: 'cors',
    headers: headers,
    body: JSON.stringify(body),
  });
  if (response.ok === 404) {
    throw new Error(HTTP_ERROR_NOT_FOUND);
  }
  if (!response.ok) {
    throw new Error(HTTP_ERROR_BAD_REQUEST);
  }
  const payload = await response.json();
  if (!payload) {
    throw new Error(HTTP_ERROR_INVALID_JSON);
  }
  return Promise.resolve(payload);
}

async function _netPatch(url, body) {
  let headers = {
    'Content-Type': 'application/json',
  };
  if (CSRF_TOKEN) {
    headers[CSRF_HEADER] = CSRF_TOKEN;
  }
  const response = await fetch(url, {
    method: 'PATCH',
    mode: 'cors',
    headers: headers,
    body: JSON.stringify(body),
  });
  if (response.ok === 404) {
    throw new Error(HTTP_ERROR_NOT_FOUND);
  }
  if (!response.ok) {
    throw new Error(HTTP_ERROR_BAD_REQUEST);
  }
  const payload = await response.json();
  if (!payload) {
    throw new Error(HTTP_ERROR_INVALID_JSON);
  }
  return Promise.resolve(payload);
}
